import React, { useState, useEffect } from 'react';
import { Globe, TrendingUp, CheckCircle, Clock, ExternalLink, BarChart3, Calendar, Download, Mail, RefreshCw, Target, Zap, Award } from 'lucide-react';

interface Citation {
  id: string;
  domain: string;
  target_domain: string;
  url?: string;
  status: 'pending' | 'ready' | 'published' | 'failed';
  created_at: string;
  updated_at: string;
}

interface KeywordPost {
  id: string;
  campaign_id: number;
  keyword: string;
  current_position?: number;
  traffic: number;
  opportunity_score: number;
  target_url?: string;
  published_url?: string;
  status: 'pending' | 'published' | 'failed';
  created_at: string;
  updated_at: string;
}

interface Campaign {
  id: string;
  domain: string;
  selected_sites: number;
  monthly_price: number;
  status: string;
  created_at: string;
  citations: Citation[];
  campaign_type?: 'citation' | 'keyword';
}

interface KeywordCampaign {
  id: number;
  order_id: string;
  domain: string;
  keywords: any[];
  selected_sites: number;
  status: string;
  created_at: string;
  updated_at: string;
  posts: KeywordPost[];
}

interface CustomerDashboardProps {
  orderId?: string;
}

const CustomerDashboard: React.FC<CustomerDashboardProps> = ({ orderId }) => {
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [keywordCampaign, setKeywordCampaign] = useState<KeywordCampaign | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [campaignType, setCampaignType] = useState<'citation' | 'keyword'>('citation');

  useEffect(() => {
    if (orderId) {
      fetchCampaignData();
    }
  }, [orderId]);

  const fetchCampaignData = async () => {
    try {
      // First try to fetch as keyword campaign
      const keywordResponse = await fetch(`${import.meta.env.VITE_API_URL}/api/orders/keyword-campaign/${orderId}`);
      
      if (keywordResponse.ok) {
        const keywordResult = await keywordResponse.json();
        if (keywordResult.success) {
          setKeywordCampaign(keywordResult.campaign);
          setCampaignType('keyword');
          setLoading(false);
          return;
        }
      }

      // If not a keyword campaign, try citation campaign
      const response = await fetch(`${import.meta.env.VITE_API_URL}/api/orders/${orderId}/status`);
      
      if (response.ok) {
        const result = await response.json();
        if (result.success) {
          setCampaign(result.data);
          setCampaignType('citation');
        } else {
          setError('Campaign not found');
        }
      } else {
        setError('Failed to load campaign data');
      }
    } catch (err) {
      setError('Unable to load campaign data');
      console.error('Campaign fetch error:', err);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading your campaign...</p>
        </div>
      </div>
    );
  }

  if (error || !campaign) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error || 'Campaign not found'}</p>
          <a href="/scanner" className="text-blue-600 hover:text-blue-700">
            Start a new scan
          </a>
        </div>
      </div>
    );
  }

  // Get current campaign data based on type
  const currentCampaign = campaignType === 'keyword' ? keywordCampaign : campaign;
  
  const publishedCitations = campaign?.citations?.filter(c => c.status === 'published') || [];
  const pendingCitations = campaign?.citations?.filter(c => c.status === 'pending') || [];
  const readyCitations = campaign?.citations?.filter(c => c.status === 'ready') || [];
  
  const publishedKeywords = keywordCampaign?.posts?.filter(p => p.status === 'published') || [];
  const pendingKeywords = keywordCampaign?.posts?.filter(p => p.status === 'pending') || [];
  const failedKeywords = keywordCampaign?.posts?.filter(p => p.status === 'failed') || [];
  
  const progressPercentage = campaignType === 'keyword' 
    ? (publishedKeywords.length / (keywordCampaign?.selected_sites || 1)) * 100
    : (publishedCitations.length / (campaign?.selected_sites || 1)) * 100;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <div className="flex items-center">
              <h1 className="text-2xl font-bold gradient-text">BIP SEO</h1>
              <span className="ml-4 text-gray-400">|</span>
              <span className="ml-4 text-lg font-semibold text-gray-700">
                {campaignType === 'keyword' ? 'Keyword Campaign Dashboard' : 'Citation Dashboard'}
              </span>
            </div>
            <div className="flex items-center space-x-4">
              <a href="/scanner" className="text-gray-700 hover:text-blue-600 px-3 py-2 rounded-md text-sm font-medium">
                Scan Another Domain
              </a>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Campaign Overview */}
        <div className="mb-8">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
                <Globe className="w-8 h-8 text-blue-600" />
                {currentCampaign?.domain}
              </h1>
              <p className="text-gray-600 mt-1">
                {campaignType === 'keyword' 
                  ? `Keyword campaign with ${keywordCampaign?.selected_sites} high-authority domains`
                  : `Citation campaign with ${campaign?.selected_sites} high-authority domains`
                }
              </p>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold text-green-600">${campaign.monthly_price}/month</div>
              <div className="text-sm text-gray-500">Active subscription</div>
            </div>
          </div>

          {/* Progress Bar */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">Campaign Progress</h3>
              <span className="text-sm text-gray-600">
                {publishedCitations.length} of {campaign.selected_sites} citations live
              </span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-4 mb-4">
              <div 
                className="bg-blue-600 h-4 rounded-full transition-all duration-500" 
                style={{ width: `${progressPercentage}%` }}
              ></div>
            </div>
            <div className="text-center text-sm text-gray-600">
              {Math.round(progressPercentage)}% Complete
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">Published</span>
              <CheckCircle className="w-4 h-4 text-green-500" />
            </div>
            <div className="text-2xl font-bold text-green-600">{publishedCitations.length}</div>
            <div className="text-sm text-gray-500">citations live</div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">Ready</span>
              <Clock className="w-4 h-4 text-blue-500" />
            </div>
            <div className="text-2xl font-bold text-blue-600">{readyCitations.length}</div>
            <div className="text-sm text-gray-500">awaiting publish</div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">In Progress</span>
              <TrendingUp className="w-4 h-4 text-orange-500" />
            </div>
            <div className="text-2xl font-bold text-orange-600">{pendingCitations.length}</div>
            <div className="text-sm text-gray-500">being created</div>
          </div>

          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm text-gray-600">Total Sites</span>
              <BarChart3 className="w-4 h-4 text-purple-500" />
            </div>
            <div className="text-2xl font-bold text-purple-600">{campaign.selected_sites}</div>
            <div className="text-sm text-gray-500">in network</div>
          </div>
        </div>

        {/* Citations List */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">
              {campaignType === 'keyword' ? 'Keyword Posts Progress' : 'Citation Progress'}
            </h3>
          </div>
          
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  {campaignType === 'keyword' ? (
                    <>
                      <th className="text-left py-3 px-6 font-semibold text-gray-900">Keyword</th>
                      <th className="text-left py-3 px-6 font-semibold text-gray-900">Status</th>
                      <th className="text-left py-3 px-6 font-semibold text-gray-900">Published URL</th>
                      <th className="text-left py-3 px-6 font-semibold text-gray-900">Created</th>
                    </>
                  ) : (
                    <>
                      <th className="text-left py-3 px-6 font-semibold text-gray-900">Citation Domain</th>
                      <th className="text-left py-3 px-6 font-semibold text-gray-900">Status</th>
                      <th className="text-left py-3 px-6 font-semibold text-gray-900">Created</th>
                      <th className="text-left py-3 px-6 font-semibold text-gray-900">Action</th>
                    </>
                  )}
                </tr>
              </thead>
              <tbody>
                {campaignType === 'keyword' ? (
                  // Keyword posts table
                  keywordCampaign?.posts?.map((post, index) => (
                    <tr key={post.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-2">
                          <Target className="w-4 h-4 text-purple-500" />
                          <span className="font-medium text-gray-900">{post.keyword}</span>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className={`px-2 py-1 rounded-full text-xs font-medium inline-block ${
                          post.status === 'published' ? 'bg-green-100 text-green-800' :
                          post.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {post.status}
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        {post.published_url ? (
                          <a
                            href={post.published_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center gap-1"
                          >
                            <ExternalLink className="w-3 h-3" />
                            View Post
                          </a>
                        ) : (
                          <span className="text-gray-400 text-sm">Not published yet</span>
                        )}
                      </td>
                      <td className="py-4 px-6 text-gray-600">
                        {new Date(post.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  )) || (
                    <tr>
                      <td colSpan={4} className="py-8 px-6 text-center text-gray-500">
                        Keyword posts are being prepared...
                      </td>
                    </tr>
                  )
                ) : (
                  // Citations table
                  campaign?.citations?.map((citation, index) => (
                    <tr key={citation.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="py-4 px-6">
                        <div className="flex items-center gap-2">
                          <Globe className="w-4 h-4 text-blue-500" />
                          <span className="font-medium text-gray-900">{citation.domain}</span>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <div className={`px-2 py-1 rounded-full text-xs font-medium inline-block ${
                          citation.status === 'published' ? 'bg-green-100 text-green-800' :
                          citation.status === 'ready' ? 'bg-blue-100 text-blue-800' :
                          citation.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-red-100 text-red-800'
                        }`}>
                          {citation.status}
                        </div>
                      </td>
                      <td className="py-4 px-6 text-gray-600">
                        {new Date(citation.created_at).toLocaleDateString()}
                      </td>
                      <td className="py-4 px-6">
                        {citation.url ? (
                          <a
                            href={citation.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-700 text-sm font-medium flex items-center gap-1"
                          >
                            <ExternalLink className="w-3 h-3" />
                            View Citation
                          </a>
                        ) : (
                          <span className="text-gray-400 text-sm">Not published yet</span>
                        )}
                      </td>
                    </tr>
                  )) || (
                    <tr>
                      <td colSpan={4} className="py-8 px-6 text-center text-gray-500">
                        Citations are being prepared...
                      </td>
                    </tr>
                  )
                )}
              </tbody>
            </table>
          </div>

          {/* Sidebar - Analytics & Insights */}
          <div className="space-y-6">
            {/* SEO Analytics Overview */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                📊 SEO Analytics Overview
              </h3>
              
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Domain Rating</span>
                  <div className="flex items-center gap-2">
                    <div className="w-16 bg-gray-200 rounded-full h-2">
                      <div className="bg-blue-500 h-2 rounded-full" style={{ width: '65%' }}></div>
                    </div>
                    <span className="text-sm font-bold text-blue-600">52</span>
                  </div>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">Organic Traffic</span>
                  <span className="text-sm font-bold text-green-600">+23%</span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">
                    {campaignType === 'keyword' ? 'Posts Published' : 'Backlinks'}
                  </span>
                  <span className="text-sm font-bold text-purple-600">
                    {campaignType === 'keyword' 
                      ? `+${publishedKeywords.length}` 
                      : `+${publishedCitations.length * 1}`
                    }
                  </span>
                </div>
                
                <div className="flex justify-between items-center">
                  <span className="text-sm text-gray-600">
                    {campaignType === 'keyword' ? 'Keywords Targeted' : 'Citations Built'}
                  </span>
                  <span className="text-sm font-bold text-orange-600">
                    {campaignType === 'keyword' 
                      ? keywordCampaign?.posts?.length || 0
                      : publishedCitations.length
                    }
                  </span>
                </div>
              </div>
            </div>

            {/* AI Insights */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                🤖 AI Insights
              </h3>
              
              <div className="space-y-3">
                <div className="p-3 bg-green-50 rounded-lg border-l-4 border-green-500">
                  <div className="font-medium text-green-800 text-sm">
                    {campaignType === 'keyword' ? 'Keyword Impact' : 'Citation Impact'}
                  </div>
                  <div className="text-green-700 text-sm">
                    {campaignType === 'keyword' 
                      ? `Your keyword posts are building authority across ${publishedKeywords.length} high-DR sites`
                      : `Your citations are building domain authority across ${publishedCitations.length} high-DR sites`
                    }
                  </div>
                </div>
                
                <div className="p-3 bg-blue-50 rounded-lg border-l-4 border-blue-500">
                  <div className="font-medium text-blue-800 text-sm">Ranking Potential</div>
                  <div className="text-blue-700 text-sm">
                    Expected 15-25% improvement in local search rankings
                  </div>
                </div>
                
                <div className="p-3 bg-purple-50 rounded-lg border-l-4 border-purple-500">
                  <div className="font-medium text-purple-800 text-sm">Network Effect</div>
                  <div className="text-purple-700 text-sm">
                    Citations spreading across {campaign.selected_sites} premium domains
                  </div>
                </div>
              </div>
            </div>

            {/* SEO Recommendations */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-bold text-gray-900 flex items-center gap-2">
                  🎯 SEO Recommendations
                </h3>
                <button className="text-blue-600 hover:text-blue-700 text-sm font-medium">
                  <RefreshCw className="w-4 h-4" />
                </button>
              </div>
              
              <div className="space-y-3">
                <div className="p-3 bg-red-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-red-800 text-sm">Optimize Title Tags</span>
                    <span className="px-2 py-1 bg-red-200 text-red-800 rounded-full text-xs font-medium">HIGH</span>
                  </div>
                  <div className="text-red-700 text-sm">
                    Improve title tags for better keyword targeting
                  </div>
                  <div className="text-xs text-red-600 mt-1">Impact: +10-15% CTR improvement</div>
                </div>
                
                <div className="p-3 bg-yellow-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="font-medium text-yellow-800 text-sm">Content Gap Analysis</span>
                    <span className="px-2 py-1 bg-yellow-200 text-yellow-800 rounded-full text-xs font-medium">MEDIUM</span>
                  </div>
                  <div className="text-yellow-700 text-sm">
                    Create content for competitor keywords
                  </div>
                  <div className="text-xs text-yellow-600 mt-1">Impact: +20-30% keyword coverage</div>
                </div>
              </div>
            </div>

            {/* Performance Chart */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                📈 Performance Trends
              </h3>
              
              <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-6 text-center">
                <div className="text-3xl font-bold text-gray-900 mb-2">
                  {Math.round(progressPercentage)}%
                </div>
                <div className="text-gray-600 mb-4">Campaign Completion</div>
                <div className="text-sm text-gray-500">
                  📊 Interactive charts coming soon
                </div>
              </div>
            </div>

            {/* Quick Actions */}
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
              <h3 className="text-lg font-bold text-gray-900 mb-4 flex items-center gap-2">
                ⚡ Quick Actions
              </h3>
              
              <div className="space-y-3">
                <button className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 px-4 rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-all flex items-center justify-center gap-2">
                  <Download className="w-4 h-4" />
                  📊 Download Full Report
                </button>
                
                <div className="p-4 bg-purple-50 border border-purple-200 rounded-lg">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    📧 Email Report:
                  </label>
                  <input
                    type="email"
                    placeholder="Enter email address"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm mb-3 focus:ring-2 focus:ring-purple-500"
                  />
                  <button className="w-full bg-gradient-to-r from-purple-600 to-pink-600 text-white py-2 px-4 rounded-lg font-semibold text-sm hover:from-purple-700 hover:to-pink-700 transition-all flex items-center justify-center gap-2">
                    <Mail className="w-4 h-4" />
                    📧 Send Report
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Next Steps */}
        <div className="mt-8 bg-gradient-to-r from-blue-600 to-purple-700 rounded-2xl p-8 text-white">
          <div className="text-center">
            <h3 className="text-2xl font-bold mb-4">What Happens Next?</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-6">
              <div className="text-center">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-xl font-bold">1</span>
                </div>
                <h4 className="font-semibold mb-2">Content Creation</h4>
                <p className="text-blue-100 text-sm">
                  Our AI creates unique citations for each domain
                </p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-xl font-bold">2</span>
                </div>
                <h4 className="font-semibold mb-2">Publication</h4>
                <p className="text-blue-100 text-sm">
                  Citations go live across our network
                </p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-white/20 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-xl font-bold">3</span>
                </div>
                <h4 className="font-semibold mb-2">Results</h4>
                <p className="text-blue-100 text-sm">
                  Watch your rankings improve over time
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerDashboard;
